// Import necessary dependencies for React hooks, API, and components
import React, { useState, useEffect } from 'react';
import Pagination from '../Tools/Pagination'; // Component for pagination controls
import AreaModal from './modals/Area/AreaModal'; // Modal component for adding/editing areas
import axiosInstance from '../../../api'; // Custom API module for making HTTP requests

// LocationDropdown component for selecting locations
const LocationDropdown = ({ onChange, value }) => {
  const [locations, setLocations] = useState([]); // Stores list of locations
  const [loading, setLoading] = useState(false); // Loading state for fetching locations

  // Fetch locations on component mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoading(true); // Set loading state
        const response = await axiosInstance.get('/ems/locations'); // Fetch locations
        setLocations(response.data?.data || response.data || []); // Handle different response formats
      } catch (error) {
        console.error('Error fetching locations:', error); // Log error
        setLocations([]); // Reset locations on error
      } finally {
        setLoading(false); // Clear loading state
      }
    };

    fetchLocations();
  }, []); // Empty dependency array ensures run on mount only

  // Render dropdown for locations
  return (
    <select
      className="form-control form-select"
      value={value}
      onChange={(e) => onChange(e.target.value)} // Trigger onChange with selected value
      disabled={loading} // Disable during loading
    >
      <option value="">All Locations</option>
      {locations.map((location) => (
        <option key={location.id} value={location.id}>
          {location.location_name}
        </option>
      ))}
    </select>
  );
};

// AreaTab component for managing EMS areas
const AreaTab = () => {
  // State management
  const [areas, setAreas] = useState([]); // Stores list of areas
  const [formData, setFormData] = useState({
    area_name: '', // Area name
    location_id: '', // Location ID
    longitude: '', // Longitude coordinate
    latitude: '' // Latitude coordinate
  }); // Form data for area creation/editing
  const [editId, setEditId] = useState(null); // ID of area being edited
  const [isLoading, setIsLoading] = useState(false); // Loading state for API calls
  
  // Filter state
  const [filterForm, setFilterForm] = useState({
    area_name: '', // Filter by area name
    location_id: '' // Filter by location ID
  }); // Filter criteria for areas
  
  // Pagination state
  const [page, setPage] = useState(1); // Current page number
  const limit = 10; // Number of items per page
  const [total, setTotal] = useState(0); // Total number of areas

  /**
   * Fetches areas from the API with pagination and filtering
   */
  const fetchAreas = async () => {
    try {
      setIsLoading(true); // Set loading state
      const params = {
        page,
        limit,
        ...filterForm // Include filter parameters
      };

      // Remove empty filter values
      Object.keys(params).forEach(key => {
        if (params[key] === '') {
          delete params[key]; // Clean up empty params
        }
      });

      const response = await axiosInstance.get('/ems/areas', { params }); // Fetch areas

      const data = response.data?.data || response.data || []; // Handle different response formats
      setAreas(data); // Update areas state
      setTotal(response.data?.total || data.length || 0); // Update total count
    } catch (error) {
      console.error('Error fetching areas:', error); // Log error
      alert('Failed to fetch areas'); // Show error alert
      setAreas([]); // Reset areas
    } finally {
      setIsLoading(false); // Clear loading state
    }
  };

  // Fetch areas when filterForm or page changes
  useEffect(() => {
    fetchAreas();
  }, [filterForm, page]); // Re-run when filterForm or page changes

  /**
   * Handles input changes for the form
   * @param {Object} e - The event object
   */
  const handleChange = (e) => {
    const { name, value } = e.target; // Extract name and value
    setFormData((prev) => ({ ...prev, [name]: value })); // Update form data
  };

  /**
   * Handles filter input changes
   * @param {Object} e - The event object
   */
  const handleFilterChange = (e) => {
    const { name, value } = e.target; // Extract name and value
    setFilterForm(prev => ({ ...prev, [name]: value })); // Update filter form
  };

  /**
   * Clears all filters and resets pagination
   */
  const clearFilters = () => {
    setFilterForm({
      area_name: '',
      location_id: ''
    }); // Reset filter fields
    setPage(1); // Reset to first page
  };

  /**
   * Opens the modal for adding a new area
   * Resets form data and clears edit ID
   */
  const openAddModal = () => {
    setEditId(null); // Clear edit ID
    setFormData({ 
      area_name: '', 
      location_id: '', 
      longitude: '', 
      latitude: '' 
    }); // Reset form data
    showModal('bs-area-modal'); // Show modal
  };

  /**
   * Prepares the modal for editing an existing area
   * @param {Object} item - The area data to edit
   */
  const handleEdit = (item) => {
    setEditId(item.id); // Set edit ID
    setFormData({ 
      area_name: item.area_name, 
      location_id: item.location_id, 
      longitude: item.longitude, 
      latitude: item.latitude 
    }); // Populate form with area data
    showModal('bs-area-modal'); // Show modal
  };

  /**
   * Shows a Bootstrap modal by ID
   * @param {string} modalId - The ID of the modal element
   */
  const showModal = (modalId) => {
    const modalEl = document.getElementById(modalId);
    if (modalEl && window.bootstrap?.Modal) {
      const modal = window.bootstrap.Modal.getOrCreateInstance(modalEl); // Initialize or get modal instance
      modal.show(); // Show modal
    } else {
      console.warn('Bootstrap modal not initialized properly.'); // Warn if modal fails
    }
  };

  /**
   * Deletes an area after confirmation
   * @param {number} id - The ID of the area to delete
   */
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this area?')) {
      try {
        await axiosInstance.delete(`/ems/areas/${id}`); // Delete area
        alert('Deleted successfully'); // Show success alert
        fetchAreas(); // Refresh area list
      } catch (err) {
        console.error('Delete failed:', err); // Log error
        alert('Failed to delete'); // Show error alert
      }
    }
  };

  /**
   * Saves or updates an area using URL-encoded payload
   */
  const handleSave = async () => {
    try {
      const payload = new URLSearchParams();
      payload.append('area_name', formData.area_name);
      payload.append('location_id', formData.location_id);
      if (formData.longitude) payload.append('longitude', formData.longitude); // Only include if provided
      if (formData.latitude) payload.append('latitude', formData.latitude); // Only include if provided

      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded' // Set content type for URL-encoded payload
      };

      if (editId) {
        await axiosInstance.put(`/ems/areas/${editId}`, payload, { headers }); // Update area
        alert('Updated successfully'); // Show success alert
      } else {
        await axiosInstance.post('/ems/areas', payload, { headers }); // Create area
        alert('Added successfully'); // Show success alert
      }

      const modalEl = document.getElementById('bs-area-modal');
      const modal = window.bootstrap.Modal.getInstance(modalEl);
      modal.hide(); // Hide modal
      fetchAreas(); // Refresh area list
    } catch (err) {
      console.error('Save failed:', err); // Log error
      alert('Failed to save'); // Show error alert
    }
  };

  // Render the component
  return (
    <div className="tab-pane active" id="area" role="tabpanel">
      {/* Filter section */}
      <div className="filter-search mt-5">
        <h6 className="px-2 mb-0">Filter</h6>
        <div className="filter-panel">
          <form className="row">
            <div className="form-group col-md-4 col-lg-4 col-12 p-2">
              <input 
                type="text" 
                className="form-control" 
                name="area_name"
                placeholder="Area Name" 
                value={filterForm.area_name}
                onChange={handleFilterChange}
              />
            </div>
            <div className="form-group col-md-4 col-lg-4 col-12 p-2">
              <LocationDropdown
                onChange={(value) => setFilterForm(prev => ({ 
                  ...prev, 
                  location_id: value // Update location_id in filter form
                }))}
                value={filterForm.location_id}
              />
            </div>
            <div className="form-group col-lg-3 p-2">
              <button 
                type="button" 
                className="btn btn-outline-primary btn-rounded"
                onClick={clearFilters}
              >
                Clear Filter
              </button>
            </div>
          </form>
          <button
            className="btn my-2 d-flex align-items-center justify-content-center btn-outline-primary"
            type="button"
            onClick={openAddModal}
          >
            <i className="ti ti-circle-plus fs-4 me-2"></i>
            Add Area
          </button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading ? (
        <div className="text-center py-4">Loading Areas...</div>
      ) : (
        <>
          {/* Areas table */}
          <div className="py-3 px-0">
            <div className="table-responsive rounded-2 ros-table">
              <table className="table text-nowrap customize-table mb-0 align-middle">
                <thead className="text-dark fs-4">
                  <tr>
                    <th><h6 className="fw-semibold mb-0">ID</h6></th>
                    <th><h6 className="fw-semibold mb-0">Area Name</h6></th>
                    <th><h6 className="fw-semibold mb-0">Location</h6></th>
                    <th><h6 className="fw-semibold mb-0">Latitude</h6></th>
                    <th><h6 className="fw-semibold mb-0">Longitude</h6></th>                    
                    <th><h6 className="fw-semibold mb-0">Action</h6></th> 
                  </tr>
                </thead>  
                <tbody>
                  {areas.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center py-4">
                        {filterForm.area_name || filterForm.location_id ? 
                         'No matching areas found' : 
                         'No Areas Found'} {/* Conditional message */}
                      </td>
                    </tr>
                  ) : (
                    areas.map((item, index) => (
                      <tr key={item.id}>
                        <td>{(page - 1) * limit + index + 1}</td> {/* Calculate display ID */}
                        <td>{item.area_name}</td>
                        <td>{item.ems_location?.location_name || 'N/A'}</td>
                        <td>{item.latitude || 'N/A'}</td>
                        <td>{item.longitude || 'N/A'}</td>
                        <td>
                          <button 
                            onClick={() => handleEdit(item)} 
                            className="h4 me-3 border-0 bg-transparent"
                          >
                            <i className="ti ti-edit text-warning"></i>
                          </button>
                          <button 
                            onClick={() => handleDelete(item.id)} 
                            className="h4 border-0 bg-transparent"
                          >
                            <i className="ti ti-trash text-danger"></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {areas.length > 0 && (
            <Pagination
              total={total}
              limit={limit}
              page={page}
              onPageChange={setPage}
            />
          )}
        </>
      )}

      {/* Modal */}
      <AreaModal
        formData={formData}
        onChange={handleChange}
        onSave={handleSave}
        editId={editId}
      />
    </div>
  );
};

// Export the component
export default AreaTab;